import React, { useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Footer from '../components/Footer';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

interface BusinessesPageProps {
  darkMode: boolean;
}

const BusinessesPage: React.FC<BusinessesPageProps> = ({ darkMode }) => {
  const location = useLocation();

  const scrollToContact = () => {
    if (location.pathname === '/') {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page with hash - HomePage will handle scrolling
      window.location.href = '/#contact';
    }
  };
  // Business Services section refs
  const businessServicesSectionRef = useRef<HTMLElement>(null);
  const businessServicesContentRef = useRef<HTMLDivElement>(null);
  
  // Individual business service refs
  const businessService1Ref = useRef<HTMLDivElement>(null);
  const businessService1ImageRef = useRef<HTMLDivElement>(null);
  const businessService2Ref = useRef<HTMLDivElement>(null);
  const businessService2ImageRef = useRef<HTMLDivElement>(null);
  const businessService3Ref = useRef<HTMLDivElement>(null);
  const businessService3ImageRef = useRef<HTMLDivElement>(null);
  const businessService4Ref = useRef<HTMLDivElement>(null);
  const businessService4ImageRef = useRef<HTMLDivElement>(null);
  const businessService5Ref = useRef<HTMLDivElement>(null);
  const businessService5ImageRef = useRef<HTMLDivElement>(null);
  const businessService6Ref = useRef<HTMLDivElement>(null);
  const businessService6ImageRef = useRef<HTMLDivElement>(null);

  // Business Services animations
  useEffect(() => {
    const setupBusinessServicesAnimations = () => {
      if (businessServicesSectionRef.current) {
        // Check if mobile device
        const isMobile = window.innerWidth < 768;
        
        // Set initial state for all business service items
        const businessServiceItems = [
          businessService1Ref.current, businessService1ImageRef.current,
          businessService2Ref.current, businessService2ImageRef.current,
          businessService3Ref.current, businessService3ImageRef.current,
          businessService4Ref.current, businessService4ImageRef.current,
          businessService5Ref.current, businessService5ImageRef.current,
          businessService6Ref.current, businessService6ImageRef.current
        ].filter(Boolean);

        gsap.set(businessServiceItems, {
          opacity: isMobile ? 1 : 0,
          y: isMobile ? 0 : 80,
          scale: isMobile ? 1 : 0.9
        });

        // Individual ScrollTrigger for Business Service 1 - Scan to BIM (only on desktop)
        if (!isMobile) {
          ScrollTrigger.create({
            trigger: businessService1Ref.current,
            start: "top 85%",
            once: true,
            onEnter: () => {
              gsap.to(businessService1Ref.current, {
                opacity: 1,
                y: 0,
                x: 0,
                duration: 0.6,
                ease: "back.out(1.7)"
              });
              gsap.to(businessService1ImageRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                ease: "power2.out"
              });
            }
          });
        }

        // Individual ScrollTrigger for Business Service 2 - Architectural and Interior Design (only on desktop)
        if (!isMobile) {
          ScrollTrigger.create({
            trigger: businessService2Ref.current,
            start: "top 85%",
            once: true,
            onEnter: () => {
              gsap.to(businessService2Ref.current, {
                opacity: 1,
                y: 0,
                x: 0,
                duration: 0.6,
                ease: "back.out(1.7)"
              });
              gsap.to(businessService2ImageRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                ease: "power2.out"
              });
            }
          });
        }

        // Individual ScrollTrigger for Business Service 3 - Architectural Visualisation and Animation (only on desktop)
        if (!isMobile) {
          ScrollTrigger.create({
            trigger: businessService3Ref.current,
            start: "top 85%",
            once: true,
            onEnter: () => {
              gsap.to(businessService3Ref.current, {
                opacity: 1,
                y: 0,
                x: 0,
                duration: 0.6,
                ease: "back.out(1.7)"
              });
              gsap.to(businessService3ImageRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                ease: "power2.out"
              });
            }
          });
        }

        // Individual ScrollTrigger for Business Service 4 - Technical Drawings (only on desktop)
        if (!isMobile) {
          ScrollTrigger.create({
            trigger: businessService4Ref.current,
            start: "top 85%",
            once: true,
            onEnter: () => {
              gsap.to(businessService4Ref.current, {
                opacity: 1,
                y: 0,
                x: 0,
                duration: 0.6,
                ease: "back.out(1.7)"
              });
              gsap.to(businessService4ImageRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                ease: "power2.out"
              });
            }
          });
        }

        // Individual ScrollTrigger for Business Service 5 - Product BIM Modelling (only on desktop)
        if (!isMobile) {
          ScrollTrigger.create({
            trigger: businessService5Ref.current,
            start: "top 85%",
            once: true,
            onEnter: () => {
              gsap.to(businessService5Ref.current, {
                opacity: 1,
                y: 0,
                x: 0,
                duration: 0.6,
                ease: "back.out(1.7)"
              });
              gsap.to(businessService5ImageRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                ease: "power2.out"
              });
            }
          });
        }

        // Individual ScrollTrigger for Business Service 6 - BIM Management (only on desktop)
        if (!isMobile) {
          ScrollTrigger.create({
            trigger: businessService6Ref.current,
            start: "top 85%",
            once: true,
            onEnter: () => {
              gsap.to(businessService6Ref.current, {
                opacity: 1,
                y: 0,
                x: 0,
                duration: 0.6,
                ease: "back.out(1.7)"
              });
              gsap.to(businessService6ImageRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                ease: "power2.out"
              });
            }
          });
        }
      }
    };

    const timer = setTimeout(setupBusinessServicesAnimations, 100);

    // Add resize listener for responsive behavior
    const handleResize = () => {
      setupBusinessServicesAnimations();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <div className="overflow-x-hidden">
        <div className="px-4 pt-4 sm:px-6 lg:px-8">
        <section
          className="relative z-10 flex min-h-[calc(100vh-2rem)] items-center justify-center overflow-hidden rounded-3xl sm:min-h-[calc(100vh-2.5rem)]"
          style={{
            backgroundImage: 'url(/images/homepage_business_op1.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8">
                FOR BUSINESSES
              </h1>
              <div className="max-w-4xl mx-auto">
                <p className="text-lg sm:text-xl text-white leading-relaxed">
                  Our architectural consultancy helps you make the most of your space — beautifully, functionally, and efficiently. With thoughtful planning from the start, we ensure your project works seamlessly, avoiding costly revisions later. Every design decision is guided by clarity, creativity, and practicality, so your vision takes shape with confidence.
                </p>
              </div>
            </div>
          </div>
        </section>
        </div>

        {/* Business Services Section */}
        <section id="business-services" ref={businessServicesSectionRef} className="py-12 sm:py-16 lg:py-20 bg-white dark:bg-gray-900 relative z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-6 lg:gap-12">
              {/* Business Service details */}
              <div ref={businessServicesContentRef} className="flex-1 w-full">
                 {/* Horizontal line at top */}
                 <div className="w-full h-px bg-gray-300 dark:bg-gray-600 mb-8"></div>
                 
                {/* Scan to BIM Service content - responsive layout */}
                <div ref={businessService1Ref} className="flex flex-col lg:flex-row gap-6 lg:gap-8 lg:items-start w-full min-h-[400px] lg:min-h-0">
                  {/* Left side - Building image */}
                  <div ref={businessService1ImageRef} className="w-full lg:w-96 h-64 sm:h-80 lg:h-96 overflow-hidden shadow-lg flex-shrink-0 mx-auto lg:mx-0 !rounded-3xl">
                    <img
                      src="/images/bim.png"
                      alt="Scan to BIM"
                      className="h-full w-full object-cover !rounded-3xl"
                    />
                  </div>
                  
                  {/* Right side - Text content */}
                  <div className="flex-1 relative flex flex-col h-auto w-full lg:w-auto gap-3 lg:gap-4">
                    <div className="w-full min-w-0">
                    <div className="text-right bg-midnight rounded-3xl p-6 sm:p-8">
                      <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 text-right">
                        Scan to BIM
                      </h3>
                      <p className="text-base sm:text-lg text-gray-300 leading-relaxed text-right">
                        We transform existing buildings into accurate digital models through advanced 3D scanning. Our Scan to BIM service provides precise data that forms the foundation for renovation, extension, or new construction projects, saving time, reducing errors, and enabling informed decision-making.
                      </p>
                    </div>
                    </div>
                    
                    {/* Bullet points at bottom */}
                    <div className="w-full flex flex-col min-w-0">
                      <ul className="grid grid-cols-2 gap-2 w-full text-right">
                        <li className="flex h-full min-h-[4.5rem] w-full items-center justify-end gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">3D Laser Scanning</span>
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] w-full items-center justify-end gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Accurate Digital Models</span>
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] w-full items-center justify-end gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">As-Built Documentation</span>
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] w-full items-center justify-end gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Facility Management</span>
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Horizontal line separator */}
                <div className="w-full h-px bg-gray-300 dark:bg-gray-600 mt-8 mb-8"></div>
                
                {/* Architectural and Interior Design Service content - responsive layout */}
                <div ref={businessService2Ref} className="flex flex-col lg:flex-row gap-6 lg:gap-8 lg:items-start w-full min-h-[400px] lg:min-h-0">
                  {/* Left side - Text content */}
                  <div className="flex-1 relative flex flex-col h-auto w-full lg:w-auto gap-3 lg:gap-4">
                    <div className="w-full min-w-0">
                    <div className="text-left bg-midnight rounded-3xl p-6 sm:p-8">
                      <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 text-left">
                        Architectural and Interior Design
                      </h3>
                      <p className="text-base sm:text-lg text-gray-300 leading-relaxed text-left">
                        Whether you need a full design solution or specific design elements, we create functional, elegant spaces that align with your brand and operational needs. From offices and commercial interiors to large-scale developments, our designs combine creativity, efficiency, and practical value.
                      </p>
                    </div>
                    </div>
                    
                    {/* Bullet points at bottom */}
                    <div className="w-full flex flex-col min-w-0">
                      <ul className="grid grid-cols-2 gap-2 w-full text-left">
                        <li className="flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Concept Development</span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Brand Integration</span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Functional Design</span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Aesthetic Excellence</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Right side - Building image */}
                  <div ref={businessService2ImageRef} className="w-full lg:w-96 h-64 sm:h-80 lg:h-96 overflow-hidden shadow-lg flex-shrink-0 mx-auto lg:mx-0 !rounded-3xl">
                    <img
                      src="/images/architectural_design.png"
                      alt="Architectural and Interior Design"
                      className="h-full w-full object-cover !rounded-3xl"
                    />
                  </div>
                </div>
                
                {/* Horizontal line separator */}
                <div className="w-full h-px bg-gray-300 dark:bg-gray-600 mt-8 mb-8"></div>
                
                {/* Architectural Visualisation and Animation Service content - responsive layout */}
                <div ref={businessService3Ref} className="flex flex-col lg:flex-row gap-6 lg:gap-8 lg:items-start w-full min-h-[400px] lg:min-h-0">
                  {/* Left side - Building image */}
                  <div ref={businessService3ImageRef} className="w-full lg:w-96 h-64 sm:h-80 lg:h-96 overflow-hidden shadow-lg flex-shrink-0 mx-auto lg:mx-0 !rounded-3xl">
                    <img
                      src="/images/office_space.png"
                      alt="Architectural Visualisation and Animation"
                      className="h-full w-full object-cover !rounded-3xl"
                    />
                  </div>
                  
                  {/* Right side - Text content */}
                  <div className="flex-1 relative flex flex-col h-auto w-full lg:w-auto gap-3 lg:gap-4">
                    <div className="w-full min-w-0">
                    <div className="text-right bg-midnight rounded-3xl p-6 sm:p-8">
                      <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 text-right">
                        Architectural Visualisation and Animation
                      </h3>
                      <p className="text-base sm:text-lg text-gray-300 leading-relaxed text-right">
                        Bring your projects to life before a single brick is laid. Our high-quality visualisations and animations communicate design intent clearly, impress stakeholders, and support marketing or investor presentations with striking realism and clarity.
                      </p>
                    </div>
                    </div>
                    
                    {/* Bullet points at bottom */}
                    <div className="w-full flex flex-col min-w-0">
                      <ul className="grid grid-cols-2 gap-2 w-full text-right">
                        <li className="flex h-full min-h-[4.5rem] w-full items-center justify-end gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Space Optimization</span>
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] w-full items-center justify-end gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Workflow Enhancement</span>
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] w-full items-center justify-end gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Employee Satisfaction</span>
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] w-full items-center justify-end gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Company Culture</span>
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Horizontal line separator */}
                <div className="w-full h-px bg-gray-300 dark:bg-gray-600 mt-8 mb-8"></div>
                
                {/* Technical Drawings Service content - responsive layout */}
                <div ref={businessService4Ref} className="flex flex-col lg:flex-row gap-6 lg:gap-8 lg:items-start w-full min-h-[400px] lg:min-h-0">
                  {/* Left side - Text content */}
                  <div className="flex-1 relative flex flex-col h-auto w-full lg:w-auto gap-3 lg:gap-4">
                    <div className="w-full min-w-0">
                    <div className="text-left bg-midnight rounded-3xl p-6 sm:p-8">
                      <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 text-left">
                        Technical Drawings
                      </h3>
                      <p className="text-base sm:text-lg text-gray-300 leading-relaxed text-left">
                        We produce detailed technical drawings that ensure construction accuracy and compliance with all standards. Our precise documentation reduces errors on site, simplifies coordination, and keeps projects on schedule and within budget.
                      </p>
                    </div>
                    </div>
                    
                    {/* Bullet points at bottom */}
                    <div className="w-full flex flex-col min-w-0">
                      <ul className="grid grid-cols-2 gap-2 w-full text-left">
                        <li className="flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Detailed Documentation</span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Construction Accuracy</span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Standards Compliance</span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Project Coordination</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Right side - Building image */}
                  <div ref={businessService4ImageRef} className="w-full lg:w-96 h-64 sm:h-80 lg:h-96 overflow-hidden shadow-lg flex-shrink-0 mx-auto lg:mx-0 !rounded-3xl">
                      <img
                        src="/images/technical_drawings.png"
                        alt="Technical Drawings"
                        className="h-full w-full object-cover !rounded-3xl"
                      />
                    </div>
                </div>
                
                {/* Horizontal line separator */}
                <div className="w-full h-px bg-gray-300 dark:bg-gray-600 mt-8 mb-8"></div>
                
                {/* Product BIM Modelling Service content - responsive layout */}
                <div ref={businessService5Ref} className="flex flex-col lg:flex-row gap-6 lg:gap-8 lg:items-start w-full min-h-[400px] lg:min-h-0">
                  {/* Left side - Building image */}
                  <div ref={businessService5ImageRef} className="w-full lg:w-96 h-64 sm:h-80 lg:h-96 overflow-hidden shadow-lg flex-shrink-0 mx-auto lg:mx-0 !rounded-3xl">
                      <img
                        src="/images/product_bim_modelling.png"
                        alt="Product BIM Modelling"
                        className="h-full w-full object-cover !rounded-3xl"
                      />
                    </div>
                    
                    {/* Right side - Text content */}
                  <div className="flex-1 relative flex flex-col h-auto w-full lg:w-auto gap-3 lg:gap-4">
                    <div className="w-full min-w-0">
                    <div className="text-right bg-midnight rounded-3xl p-6 sm:p-8">
                      <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 text-right">
                        Product BIM Modelling
                      </h3>
                      <p className="text-base sm:text-lg text-gray-300 leading-relaxed text-right">
                        Develop comprehensive BIM models for products and components that integrate seamlessly with building systems. Our detailed 3D models enable better coordination, reduce conflicts, and streamline the construction process.
                      </p>
                    </div>
                    </div>
                    
                    {/* Bullet points at bottom */}
                    <div className="w-full flex flex-col min-w-0">
                      <ul className="grid grid-cols-2 gap-2 w-full text-right">
                        <li className="flex h-full min-h-[4.5rem] w-full items-center justify-end gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">3D Component Models</span>
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] w-full items-center justify-end gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">System Integration</span>
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] w-full items-center justify-end gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Conflict Resolution</span>
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] w-full items-center justify-end gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Construction Streamlining</span>
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Horizontal line separator */}
                <div className="w-full h-px bg-gray-300 dark:bg-gray-600 mt-8 mb-8"></div>
                
                {/* BIM Management and Environment Implementation Service content - responsive layout */}
                <div ref={businessService6Ref} className="flex flex-col lg:flex-row gap-6 lg:gap-8 lg:items-start w-full min-h-[400px] lg:min-h-0">
                  {/* Left side - Text content */}
                  <div className="flex-1 relative flex flex-col h-auto w-full lg:w-auto gap-3 lg:gap-4">
                    <div className="w-full min-w-0">
                    <div className="text-left bg-midnight rounded-3xl p-6 sm:p-8">
                      <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 text-left">
                        BIM Management and Environment Implementation
                      </h3>
                      <p className="text-base sm:text-lg text-gray-300 leading-relaxed text-left">
                        Implement comprehensive BIM workflows and standards across your organization. We establish robust BIM environments, train your teams, and ensure consistent data management throughout the project lifecycle.
                      </p>
                    </div>
                    </div>
                    
                    {/* Bullet points at bottom */}
                    <div className="w-full flex flex-col min-w-0">
                      <ul className="grid grid-cols-2 gap-2 w-full text-left">
                        <li className="flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Workflow Implementation</span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Team Training</span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Data Management</span>
                        </li>
                        <li className="flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-black bg-white px-3 sm:px-4 py-2.5 text-gray-900">
                          <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-black"></span>
                          <span className="min-w-0 flex-1 text-sm sm:text-base text-gray-900">Standards Compliance</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Right side - Building image */}
                  <div ref={businessService6ImageRef} className="w-full lg:w-96 h-64 sm:h-80 lg:h-96 overflow-hidden shadow-lg flex-shrink-0 mx-auto lg:mx-0 !rounded-3xl">
                    <img
                      src="/images/BIM Management and Environment Implementation.png"
                      alt="BIM Management and Environment Implementation"
                      className="h-full w-full object-cover !rounded-3xl"
                    />
                  </div>
                </div>
                
                {/* Horizontal line at bottom */}
                <div className="w-full h-px bg-gray-300 dark:bg-gray-600 mt-8"></div>

              </div>
            </div>
          </div>
        </section>

        {/* Back Button Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gray-50 dark:bg-gray-800 relative z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={() => window.location.href = '/'}
                className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-2xl text-white bg-midnight hover:bg-zinc-800 dark:bg-white dark:text-midnight dark:hover:bg-gray-200 transition-all duration-300 hover:scale-105"
              >
                ← Back to Main Page
              </button>
              <button 
                onClick={scrollToContact}
                className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-2xl text-white bg-midnight hover:bg-zinc-800 dark:bg-white dark:text-midnight dark:hover:bg-gray-200 transition-all duration-300 hover:scale-105"
              >
                Ready to start? →
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default BusinessesPage;
